import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterItem,
  CharacterSpell,
  PseudoItem,
  Spell,
  SummonOption2,
} from "@dndModels";
import { ModelBase } from "@models";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import FilterBox from "@dndComponents/filtering/FilterBox";
import CharacterListOptionsInput from "@dndComponents/character/misc/CharacterListOptionsInput";
import StringBox from "@components/input/strings/StringBox";
import LoadingBox from "@components/input/LoadingBox";

interface Props {
  character: Character;
  char_item: CharacterItem;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
// const common_util = DataUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function SpellStoringItemControls({
  character,
  char_item,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  const [spells, setSpells] = useState<Spell[]>([]);
  const [filtered, setFiltered] = useState<Spell[]>([]);

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      const new_spells = data_util.spells.filter(o => o.level > 0 && o.level < 4);
      setSpells(new_spells);
      setFiltered(new_spells);
      if (char_item.spell_storing_spell.spell_id !== "") {
        console.log(char_item.spell_storing_spell);
      } else if (char_item.spell_storing_spell.spell_dc === 0) {
        let max_dc = 0;
        let max_attack = 0;
        let max_mod = 0;
        for (const char_class of character.classes) {
          max_dc = Math.max(max_dc, char_class.spell_dc);
          max_attack = Math.max(max_attack, char_class.spell_attack);
          max_mod = Math.max(max_mod, char_class.spellcasting_modifier);
        }
        char_item.spell_storing_spell.spell_dc = max_dc;
        char_item.spell_storing_spell.spell_attack = max_attack;
        char_item.spell_storing_spell.spell_mod = max_mod;
        char_item.spell_storing_spell.castings = max_mod * 2;
        setPing(!ping);
      }
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const updateFilter = (changed: any) => {
    const use_filter = {...changed};
    let new_filtered = [...spells];
    if (use_filter.known !== undefined) {
      const known_spell_ids = character.spellbook_spells.map(o => o.spell_id);
      known_spell_ids.push(...character.spells.map(o => o.spell_id));
      if (use_filter.known) {
        new_filtered = new_filtered.filter(o => known_spell_ids.includes(o._id));
      } else {
        new_filtered = new_filtered.filter(o => !known_spell_ids.includes(o._id));
      }
      delete use_filter.known;
    }
    // debugger;
    new_filtered = api.filter("spell", new_filtered, use_filter) as Spell[];
    setFiltered(new_filtered);
    setReloading(true);
  }

  if (!fetched) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <FilterBox
            data_type="spell"
            flat
            dont_use_api
            all_objects={spells}
            character_options={character.character_options}
            the_filter={{ search_string: [""] }}
            extra_fields={["known"]}
            quick_fields={["school","spell_level","spell_list","casting_time","known"]}
            onChange={updateFilter}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { !reloading &&
            <CharacterListOptionsInput
              data_type="spell"
              character={character}
              use_objects={filtered}
              selected={ char_item.spell_storing_spell.spell_id !== "" ? [char_item.spell_storing_spell.spell_id] : [] } 
              choose_max={1}
              sort={(obj1: ModelBase | CharacterItem | CharacterSpell | SummonOption2 | PseudoItem, obj2: ModelBase | CharacterItem | CharacterSpell | SummonOption2 | PseudoItem) => {
                if (obj1 instanceof Spell && obj2 instanceof Spell) {
                  if (obj1.level === obj2.level) {
                    return obj1.name.localeCompare(obj2.name);
                  } else {
                    return obj1.level > obj2.level ? 1 : -1;
                  }
                }
                return 0;
              }}
              force_selectable
              onChange={(changed: string[]) => {
                char_item.spell_storing_spell.spell_id = changed[0];
                if (changed.length === 0) {
                  char_item.spell_storing_spell.the_spell = undefined;
                } else {
                  const spell = spells.find(o => char_item.spell_storing_spell.spell_id === o._id);
                  if (spell) {
                    char_item.spell_storing_spell.the_spell = spell;
                    char_item.spell_storing_spell.level = spell.level;
                  }
                }
                character.changed();
                character.subscription_holder.itemsChanged();
                char_util.repopulateCharFeatureBases(character);
                setPing(!ping);
              }}
            />
          }
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox
            name="Spell Attack"
            value={`${char_item.spell_storing_spell.spell_attack}`}
            type="number"
            onBlur={(changed: string) => {
              char_item.spell_storing_spell.spell_attack = +changed;
              character.changed();
              character.subscription_holder.itemsChanged();
              char_util.repopulateCharFeatureBases(character);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox
            name="Spell DC"
            value={`${char_item.spell_storing_spell.spell_dc}`}
            type="number"
            onBlur={(changed: string) => {
              char_item.spell_storing_spell.spell_dc = +changed;
              character.changed();
              character.subscription_holder.itemsChanged();
              char_util.repopulateCharFeatureBases(character);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox
            name="Spell Modifier"
            value={`${char_item.spell_storing_spell.spell_mod}`}
            type="number"
            onBlur={(changed: string) => {
              char_item.spell_storing_spell.spell_mod = +changed;
              character.changed();
              character.subscription_holder.itemsChanged();
              char_util.repopulateCharFeatureBases(character);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <StringBox
            name="Total Castings"
            value={`${char_item.spell_storing_spell.castings}`}
            type="number"
            onBlur={(changed: string) => {
              char_item.spell_storing_spell.castings = +changed;
              character.changed();
              character.subscription_holder.itemsChanged();
              char_util.repopulateCharFeatureBases(character);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <StringBox
            name="Has Been Cast"
            value={`${char_item.spell_storing_spell.has_been_cast}`}
            type="number"
            onBlur={(changed: string) => {
              char_item.spell_storing_spell.has_been_cast = +changed;
              character.changed();
              character.subscription_holder.itemsChanged();
              char_util.repopulateCharFeatureBases(character);
              setPing(!ping);
            }}
          />
        </Grid>
      </Grid>
    );
  }
}
